import { useCallback, useRef, useState } from 'react';
import { useMaintenanceWarning } from './useMaintenanceWarning';

/**
 * Submit policy shared by every add-repo modal: re-entry guard (ref, so it survives two
 * clicks in one tick), maintenance warning, and release on every exit path.
 * Callers validate first, then call runSubmit(task).
 */
export function useRepoSubmit(taskName) {
    const { warnIfNeeded, WarningModal } = useMaintenanceWarning();
    const submittingRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const release = useCallback(() => {
        submittingRef.current = false;
        setIsSubmitting(false);
    }, []);

    // Returns false if the click was ignored as a repeat.
    const runSubmit = useCallback(
        (task) => {
            if (submittingRef.current) {
                return false;
            }
            submittingRef.current = true;
            setIsSubmitting(true);

            // 3rd arg matters: the warning's cancel path never runs the callback, so
            // without it a dismissed modal leaves the button latched for good.
            warnIfNeeded(
                taskName,
                async () => {
                    try {
                        await task();
                    } finally {
                        release();
                    }
                },
                release
            );
            return true;
        },
        [warnIfNeeded, taskName, release]
    );

    return { isSubmitting, runSubmit, WarningModal };
}
